<template>
  <div class="flex h-full min-h-0 flex-col">
    <div class="flex shrink-0 items-center gap-2 px-3 pt-3">
      <UButton
        color="neutral"
        variant="ghost"
        size="md"
        :class="SHEET_LEADING_CONTROL_CLASS"
        icon="i-lucide-x"
        :aria-label="t('general.close')"
        @click="onCancel"
      />

      <h2 class="flex-1 truncate text-panel-title">{{ t('general.select_customer') }}</h2>

      <UButton
        color="primary"
        variant="ghost"
        icon="i-lucide-plus"
        @click="openItemForm()"
      >
        {{ t('general.add') }}
      </UButton>
    </div>

    <div class="shrink-0 px-4 py-2">
      <UInput
        ref="searchRef"
        v-model="itemSearch"
        type="search"
        class="w-full"
        variant="search"
        :placeholder="t('general.search')"
      />
    </div>

    <div class="min-h-0 flex-1">
      <dynamic-scroller
        v-if="filteredItems.length"
        class="scroller"
        :items="filteredItems"
        :min-item-size="70"
        key-field="id"
      >
        <template v-slot="{ item, index, active }">
          <dynamic-scroller-item
            :item="item"
            :active="active"
            :data-index="index"
          >
            <!-- A card-only buyer (discount + buyer card) cannot be picked by
                 tapping — only by typing the card number into the search. The
                 row says so BEFORE the tap: checkbox + details at the design's
                 disabled strength (the menu-option recipe), no hover, and the
                 way out on an accent chip; the ⋮ menu stays live where the
                 role may manage the buyer. -->
            <div
              data-test="client-row"
              class="flex min-h-17.5 w-full items-center gap-3 px-4"
              :class="[
                { 'border-b border-default': index !== filteredItems.length - 1 },
                isLocked(item) ? 'cursor-default' : 'cursor-pointer hover:bg-elevated',
              ]"
              :aria-disabled="isLocked(item) || undefined"
              @click="selectItem(item, $event)"
            >
              <UCheckbox
                v-if="selectable"
                class="pointer-events-none shrink-0"
                :class="{ 'opacity-45': isLocked(item) }"
                :model-value="isChecked(item.client)"
                :disabled="isLocked(item)"
              />

              <div class="min-w-0 flex-1 py-2">
                <div :class="{ 'opacity-45': isLocked(item) }">
                  <div class="truncate">{{ item.client.name }}</div>
                  <p v-if="item.client.address" class="text-body text-muted">{{ item.client.address }}</p>
                  <p v-if="item.client.city" class="text-body text-muted">
                    {{ item.client.city }}<span v-if="item.client.country">, {{ item.client.country }}</span>
                  </p>
                  <p v-if="item.client.id_number" class="text-body text-muted">
                    {{ `${item.client.id_type === ClientIDType.tax_id_number ? t('general.tax_id_number') : t('general.id_number')}: ${item.client.id_number}` }}
                  </p>
                  <p v-if="item.client.pricelist_id" class="text-body text-muted">
                    {{ `${t('general.pricelist')}: ${Pricelist.find(item.client.pricelist_id)?.name}` }}
                  </p>
                  <p v-if="item.client.getDiscountPercent()" class="text-body text-muted">
                    {{ `${t('general.discount')}: ${item.client.getDiscountPercent()}%` }}
                  </p>
                  <strong v-if="item.client.getDebt().toUnit() > 0" class="text-body text-error">
                    {{ `${t('general.installments_debt')}: ${item.client.getDebt().toFormat()}` }}
                  </strong>
                </div>
                <!-- The way out rides an accent chip (owner 2026-09-16: "a chip or
                     a different color", not muted text) — the chip recipe with
                     the accent tint + accent text, like an active pill. -->
                <span v-if="isLocked(item)" class="chip-pill mt-1 bg-(--app-tint-accent) text-(--app-text-accent)">
                  <UIcon name="i-lucide-credit-card" class="size-3.5 shrink-0" />
                  {{ t('clients.card_required_hint') }}
                </span>
              </div>

              <UButton
                v-if="itemMenuOptionsFor(item.client).length"
                color="neutral"
                variant="ghost"
                icon="i-lucide-ellipsis"
                class="shrink-0"
                :ui="{ leadingIcon: 'size-5' }"
                :aria-label="t('general.other_options')"
                @click="openItemMenu(item.client, $event)"
              />
            </div>
          </dynamic-scroller-item>
        </template>
      </dynamic-scroller>

      <CodContextMenu
        v-model="showItemMenu"
        :options="itemMenuOptions"
        :event="menuEvent || null"
        @select="onItemMenuSelect($event.action)"
      />
    </div>

    <div class="flex min-h-17 shrink-0 items-center gap-2 px-4 py-3">
      <UButton
        color="neutral"
        variant="outline"
        size="lg-plus"
        class="flex-1"
        @click="onCancel"
      >
        {{ t('general.cancel') }}
      </UButton>

      <UButton
        color="primary"
        variant="solid"
        size="lg-plus"
        icon="i-lucide-check"
        class="flex-1"
        @click="onSelect(selectedItems)"
      >
        {{ t('general.confirm') }}
      </UButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType, Ref, computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Client, ClientIDType, Pricelist, PrintRoleType, UserService, apiService, printService } from 'app-core';
import { useNumpad } from '@/composables/useNumpad';
import { useToast } from '@/composables/useToast';
import { ICodConfirmConfig, SHEET_LEADING_CONTROL_CLASS } from 'app-components';
import { useAppStore } from '@/stores/useAppStore';
import { useClient } from '@/composables/useClient';
import CodContextMenu from '@/components/CodContextMenu.vue';
import { useUtilities } from 'app-components';

interface IItem {
  id: number | null
  client: Client
  disabled: boolean
}

const props = defineProps({
  multiple: {
    type: Boolean,
    default: false
  },
  selectable: {
    type: Boolean,
    default: false
  },
  selected: {
    type: Array as PropType<Client[]>,
    default: () => []
  },
  onSelect: {
    type: Function as PropType<(selected: Client[]) => void>,
    default: () => {}
  },
  onCancel: {
    type: Function as PropType<() => void>,
    default: () => {}
  },
  openItemForm: {
    type: Function as PropType<(client?: Client) => void>,
    default: () => {}
  }
})

const { t } = useI18n()
const { toast } = useToast()
const { addClientInstallment } = useNumpad()
const { updatedClient } = useClient()
const { debounce } = useUtilities();
const store = useAppStore()
const codConfirm = inject('codConfirm') as (config: ICodConfirmConfig) => void

const searchRef = ref<{ inputRef?: HTMLInputElement | null } | null>(null)

const itemSearch = ref('')

const getAllItems = () => Client.all()
const matchItem = (item: Client, searchQuery: string) => item.matches(searchQuery)
const isSame = (item1: Client, item2: Client) => item1.id === item2.id
const isChecked = (item: Client) => selectedItems.value.findIndex(i => isSame(i, item)) !== -1

const items: Ref<Client[]> = ref(getAllItems())
const selectedItems: Ref<Client[]> = ref(props.selected || [])
const filteredItems: Ref<IItem[]> = ref(getAllItems().map(client => ({ id: client.id, client, disabled: false })))

/** `disabled` is the list-time verdict (card-only and the search is not the
 *  card); the row is LOCKED only while that buyer is not selected — selecting
 *  by card clears the search and rebuilds the list, and the selected buyer
 *  must read at full strength. Toggling the selection off locks it again. */
const isLocked = (item: IItem) => item.disabled && !isChecked(item.client)

const showItemMenu = ref(false)
const menuEvent: Ref<PointerEvent | undefined> = ref()
const selectedItem: Ref<Client | undefined> = ref()

setTimeout(() => {
  searchRef.value?.inputRef?.focus()
}, 750)

watch(itemSearch, (val, oldVal) => {
  filterList(val)
})

watch(() => updatedClient.value, val => {
  if(!val) return

  items.value = getAllItems()
  selectedItems.value = [val]
  filterList(itemSearch.value)
}, { deep: true })

/** A card-only buyer (discount + buyer card) can only be attached by typing
 *  the card number, so staff without admin access must not be able to edit
 *  the card or the discount away and then take the discount by tapping.
 *  Collecting installments is a payment, not an edit — it stays available. */
const isCardOnly = (client: Client) => client.getDiscountPercent() > 0 && !!client.card
const canManage = (client: Client) => !isCardOnly(client) || UserService.allows('nav_access_admin')

function itemMenuOptionsFor(client?: Client) {
  if (!client) return []
  return [
    { text: t('general.edit'), data: { action: 'edit' }, icon: 'i-lucide-pencil', isEnabled: () => canManage(client) },
    { text: t('general.installments_pay'), data: { action: 'installment' }, icon: 'i-lucide-banknote', isEnabled: () => client.getDebt().toUnit() > 0 },
    { text: t('general.delete'), data: { action: 'delete' }, icon: 'i-lucide-trash-2', role: 'destructive', isEnabled: () => canManage(client) },
  ].filter(itemMenuOption => itemMenuOption.isEnabled())
}

const itemMenuOptions = computed(() => itemMenuOptionsFor(selectedItem.value))

function openItemMenu(item: Client, ev?: MouseEvent) {
  ev?.stopPropagation()
  ev?.preventDefault()

  menuEvent.value = ev as PointerEvent | undefined
  selectedItem.value = item
  showItemMenu.value = true
}

async function onItemMenuSelect(action: string) {
  showItemMenu.value = false

  if(!selectedItem.value) return

  if(action === 'edit') {
    props.openItemForm(selectedItem.value)
    return
  }

  if(action === 'installment') {
    const amount = await addClientInstallment(selectedItem.value.getDebt().toUnit())
    if(amount) {
      addInstallment(amount)
    }
  }

  if(action === 'delete') {
    deleteClient(selectedItem.value)
  }
}

/**
 * Update the rendered view with the provided search query.
 * If no query is provided, all data will be rendered.
 */
const filterList = debounce((searchQuery: string = '') => {
  searchQuery = searchQuery.trim().toLowerCase();

  const clientCardFound = items.value.find(item => item.card === searchQuery)
  if (clientCardFound) {
    const item = {
      id: clientCardFound.id,
      client: clientCardFound,
      disabled: !!(clientCardFound.getDiscountPercent() > 0 && clientCardFound.card && searchQuery !== clientCardFound.card)
    }

    itemSearch.value = ''
    filteredItems.value = [item]

    if (!selectedItems.value.some(c => c.id === clientCardFound.id)) {
      selectItem(item)
    }

    return
  }

  // Separate already selected items and non-selected items
  const alreadySelected = items.value.filter(item => selectedItems.value.findIndex(i => isSame(i, item)) !== -1);
  const nonSelected = items.value.filter(item => selectedItems.value.findIndex(i => isSame(i, item)) === -1);

  // Filter out the unselected items based on the search query
  const filteredNonSelected = searchQuery
    ? nonSelected.filter(item => matchItem(item, searchQuery))
    : nonSelected;

  // Concatenate the already selected items with the filtered items
  filteredItems.value = [...alreadySelected, ...filteredNonSelected].map(client => {
    return {
      id: client.id,
      client,
      disabled: !!(client.getDiscountPercent() > 0 && client.card && searchQuery !== client.card)
    }
  });
}, 200)

filterList()

const selectItem = (item: IItem, ev?: MouseEvent) => {
  if(!props.selectable) {
    if (canManage(item.client)) props.openItemForm(item.client)
    return
  }

  ev?.stopPropagation()
  ev?.preventDefault()

  if(isLocked(item)) {
    toast(t('order.buyer_card_needed'), 'error');
    return
  }

  const alreadyChecked = selectedItems.value.findIndex(i => isSame(i, item.client)) !== -1

  if(!props.multiple) {
    selectedItems.value = !alreadyChecked ? [item.client] : [];
  } else {
    selectedItems.value = !alreadyChecked
      ? [...selectedItems.value, item.client]
      : selectedItems.value.filter(i => !isSame(i, item.client))
  }
}

async function deleteClient(client: Client) {
  codConfirm({
    title: t('general.delete'),
    message: t('clients.permanently_delete'),
    buttonTrueText: t('general.yes'),
    buttonCancelText: t('general.no'),
    yesHandler: async () => {
      try {
        await apiService.delete(`/clients/${client.id}`, {})

        client.$delete()
        items.value = getAllItems()
        filterList(itemSearch.value)

        toast(t('clients.deleted'), 'success');
      } catch (error: any) {
        if (error.response.status === 400) {
          const errorCode = error.response?.data?.errors?.find((item: { code: string }) => !!item.code)?.code
          if(errorCode) toast(t(`clients.${errorCode}`), 'error');
        }
      }
    }
  })
}

async function addInstallment(value: number) {
  codConfirm({
    title: t('general.installments_pay'),
    message: t('general.are_you_sure'),
    buttonTrueText: t('general.yes'),
    buttonCancelText: t('general.no'),
    yesHandler: async () => {
      if(!selectedItem.value) return

      const result = await selectedItem.value?.addInstallment(value)
      if(!result.success) {
        toast(result?.error || t('general.action_failed'), 'error')
        return
      }

      const receiptPrinter = printService.getAssignedPrinter(PrintRoleType.receipt);
      const useReceiptPrinter = !!receiptPrinter && store.device?.platform !== 'web';

      printService.print(selectedItem.value.getInstallmentPrintable(value), {
        printer: useReceiptPrinter ? receiptPrinter : undefined,
        choosePrinter: !useReceiptPrinter,
      })

      selectedItem.value = undefined

      toast(t('general.installments_paid'), 'success');
    }
  })
}
</script>

<style lang="scss" scoped>
.scroller {
  height: 100%;
}
</style>
